import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BarChart3, Repeat, Users, Car, TrendingUp, AlertTriangle, DollarSign, Clock } from "lucide-react";

const RANGES = [
  ["today", "Today"], ["week", "7 days"], ["month", "30 days"],
  ["quarter", "Quarter"], ["year", "Year"],
];

const Kpi = ({ icon: Icon, label, value, sub, tone = "cyan" }) => (
  <div className="rounded-2xl border border-white/12 p-4 bg-black/30">
    <div className="flex items-center gap-2 text-white/50 label-caps text-[9px]">
      <Icon size={11} className={`text-${tone}`} /> {label}
    </div>
    <div className="text-white text-2xl font-serif mt-1">{value}</div>
    {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);

export default function AnalyticsTab() {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [abandoned, setAbandoned] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/analytics/summary?range=${range}`),
      api.get(`/admin/analytics/abandoned`),
    ])
      .then(([s, a]) => { setData(s.data); setAbandoned(a.data || []); })
      .catch(() => toast.error("Couldn't load analytics"))
      .finally(() => setLoading(false));
  }, [range]);

  const t = data?.totals;

  return (
    <div data-testid="bo-analytics-root" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-cyan text-[11px] mb-1">Insight</div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            <BarChart3 className="text-cyan" size={20} /> Analytics
          </h2>
        </div>
        <div className="flex gap-1 rounded-full border border-white/10 p-1 bg-black/30">
          {RANGES.map(([k, l]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              data-testid={`bo-analytics-range-${k}`}
              className={`px-3 py-1.5 rounded-full text-[10px] label-caps ${
                range === k ? "bg-cyan text-black" : "text-white/60 hover:text-white"
              }`}
            >{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-center py-12">Loading…</div>
      ) : t ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={DollarSign} label="Revenue" value={`$${t.revenue.toLocaleString()}`} sub={`Avg $${t.avg_rental_value.toLocaleString()}`} />
            <Kpi icon={Car} label="Confirmed bookings" value={t.confirmed} sub={`${t.conversion_rate_pct}% conversion`} />
            <Kpi icon={Repeat} label="Repeat customer" value={`${t.repeat_customer_rate_pct}%`} sub={`${t.unique_customers} unique`} />
            <Kpi icon={Clock} label="Avg rental" value={`${t.avg_rental_duration_days} d`} sub="Rental length" />
            <Kpi icon={TrendingUp} label="Rental revenue" value={`$${t.rental_revenue.toLocaleString()}`} />
            <Kpi icon={Users} label="Chauffeur revenue" value={`$${t.ride_revenue.toLocaleString()}`} />
            <Kpi icon={AlertTriangle} label="Abandoned" value={t.abandoned} sub={`${t.abandonment_rate_pct}% of started`} />
            <Kpi icon={BarChart3} label="Started" value={t.bookings} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
              <div className="label-caps text-cyan text-[10px] mb-3">Top rented vehicles</div>
              {(data.top_vehicles || []).slice(0, 6).map((v) => (
                <div key={v.vehicle_id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/8">
                  <span className="text-white">{v.vehicle_name || v.vehicle_id}</span>
                  <span className="text-white/60">{v.count} · ${v.revenue.toLocaleString()}</span>
                </div>
              ))}
              {!(data.top_vehicles || []).length && <div className="text-white/40 text-xs">No confirmed bookings in window.</div>}
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="label-caps text-red-300 text-[10px]">Abandoned bookings (older than 45 min)</div>
                <span className="text-white/40 text-[10px]">{abandoned.length}</span>
              </div>
              {abandoned.length === 0 ? (
                <div className="text-white/40 text-xs">Nothing abandoned right now.</div>
              ) : abandoned.slice(0, 8).map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/8">
                  <div className="min-w-0">
                    <div className="text-white truncate">{b.full_name || "—"} · {b.vehicle_name}</div>
                    <div className="text-white/40 text-[10px] truncate">{b.customer_email || b.customer_phone || ""}</div>
                  </div>
                  <span className="text-red-300 text-[10px]">${Number(b.amount || 0).toFixed(0)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
